import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import api from "../../services/api";
import { LoadingOverlay, RECEIVABLES_API, todayIso, useReceivableLookups } from "./common";

/** A build-time key wins; otherwise the key configured on the server (app.google-maps.api-key) is used, like the legacy page. */
const BUILD_MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// The activity strip runs 8 AM to 7 PM, the same window the server places the dots in.
const TIMELINE_HOURS = [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19];
const hourLabel = (hour) => `${hour % 12 === 0 ? 12 : hour % 12} ${hour < 12 ? "AM" : "PM"}`;

const LEGEND = [
  { color: "#10b981", label: "Payment" },
  { color: "#ffcc5c", label: "Phone change" },
  { color: "#3b82f6", label: "Login" },
  { color: "#9b59b6", label: "Denominations" },
  { color: "#e74c3c", label: "Shop close" },
  { color: "#95a5a6", label: "Location update" },
];

let mapsLoader = null;
const loadGoogleMaps = (apiKey) => {
  if (window.google?.maps) return Promise.resolve(window.google.maps);
  if (!mapsLoader) {
    mapsLoader = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}`;
      script.async = true;
      script.onload = () => resolve(window.google.maps);
      script.onerror = () => {
        mapsLoader = null;
        reject(new Error("Could not load Google Maps"));
      };
      document.head.appendChild(script);
    });
  }
  return mapsLoader;
};

/** Google map with a route line and numbered, colour-coded pins (one per distinct coordinate). */
const GoogleRouteMap = ({ points, apiKey }) => {
  const ref = useRef(null);

  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps(apiKey)
      .then((maps) => {
        if (cancelled || !ref.current) return;
        const path = points.map((p) => ({ lat: Number(p.lat), lng: Number(p.lng) }));
        const map = new maps.Map(ref.current, { center: path[0], zoom: 14, mapTypeControl: false, streetViewControl: false });
        const bounds = new maps.LatLngBounds();
        new maps.Polyline({ path, map, strokeColor: "#4f46e5", strokeOpacity: 0.7, strokeWeight: 3 });
        points
          .filter((p) => p.firstAtCoordinate)
          .forEach((p) => {
            const position = { lat: Number(p.lat), lng: Number(p.lng) };
            bounds.extend(position);
            new maps.Marker({
              position,
              map,
              title: p.tooltip,
              label: p.sequenceLabel ? { text: String(p.sequenceLabel), color: "#fff", fontSize: "11px", fontWeight: "700" } : undefined,
              icon: { path: maps.SymbolPath.CIRCLE, scale: 11, fillColor: p.color, fillOpacity: 1, strokeColor: "#fff", strokeWeight: 2 },
            });
          });
        if (!bounds.isEmpty()) map.fitBounds(bounds);
      })
      .catch((error) => toast.error(error.message));
    return () => {
      cancelled = true;
    };
  }, [points, apiKey]);

  return <div ref={ref} style={{ width: "100%", height: 420, borderRadius: 12, border: "1px solid var(--color-border)" }} />;
};

/** Map-less fallback: the same route and pins projected onto an SVG. */
const SvgRouteMap = ({ points }) => {
  const width = 900;
  const height = 420;
  const pad = 28;
  const lats = points.map((p) => Number(p.lat));
  const lngs = points.map((p) => Number(p.lng));
  const [minLat, maxLat, minLng, maxLng] = [Math.min(...lats), Math.max(...lats), Math.min(...lngs), Math.max(...lngs)];
  const spanLat = maxLat - minLat || 0.001;
  const spanLng = maxLng - minLng || 0.001;
  const project = (p) => [
    pad + ((Number(p.lng) - minLng) / spanLng) * (width - pad * 2),
    height - pad - ((Number(p.lat) - minLat) / spanLat) * (height - pad * 2),
  ];
  const line = points.map((p) => project(p).join(",")).join(" ");

  return (
    <svg viewBox={`0 0 ${width} ${height}`} style={{ width: "100%", background: "#f8fafc", borderRadius: 12, border: "1px solid var(--color-border)" }}>
      <polyline points={line} fill="none" stroke="#4f46e5" strokeOpacity="0.6" strokeWidth="2.5" />
      {points
        .filter((p) => p.firstAtCoordinate)
        .map((p, index) => {
          const [x, y] = project(p);
          return (
            <g key={`${p.lat}_${p.lng}_${index}`}>
              <title>{p.tooltip}</title>
              <circle cx={x} cy={y} r="11" fill={p.color} stroke="#fff" strokeWidth="2" />
              <text x={x} y={y + 4} textAnchor="middle" fontSize="11" fontWeight="700" fill="#fff">
                {p.sequenceLabel}
              </text>
            </g>
          );
        })}
    </svg>
  );
};

const StatCard = ({ label, value }) => (
  <div className="stat-card">
    <div className="stat-card-label">{label}</div>
    <div className="stat-card-value">{value}</div>
  </div>
);

/** A field user's day on the map (legacy location_summary.aspx). */
const LocationSummary = () => {
  const { fieldUsers } = useReceivableLookups(["fieldUsers"]);
  const [userId, setUserId] = useState("");
  const [fromDate, setFromDate] = useState(todayIso());
  const [toDate, setToDate] = useState(todayIso());
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mapsKey, setMapsKey] = useState(BUILD_MAPS_KEY || "");

  useEffect(() => {
    if (BUILD_MAPS_KEY) return;
    api
      .get(`${RECEIVABLES_API}/locations/map-config`)
      .then((config) => setMapsKey(config?.googleMapsApiKey || ""))
      .catch(() => setMapsKey(""));
  }, []);

  const load = async () => {
    if (!userId) {
      toast.error("Select a person");
      return;
    }
    if (fromDate > toDate) {
      toast.error("From date cannot be after to date");
      return;
    }
    setLoading(true);
    try {
      setSummary(await api.get(`${RECEIVABLES_API}/locations/summary`, { userId, fromDate, toDate }));
    } catch (error) {
      toast.error(error.message || "Failed to load location summary");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4" style={{ position: "relative" }}>
      <LoadingOverlay show={loading} label="Loading route..." />
      <div className="filters-bar">
        <select value={userId} onChange={(e) => setUserId(e.target.value)} aria-label="Person">
          <option value="">-- Select person --</option>
          {fieldUsers.map((u) => (
            <option key={u.userId} value={u.userId}>
              {u.name} ({u.role === "DELIVERY_BOY" ? "Delivery" : "Salesman"})
            </option>
          ))}
        </select>
        <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} aria-label="From date" />
        <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} aria-label="To date" />
        <button className="primary" onClick={load} disabled={loading}>
          Generate
        </button>
      </div>

      {summary && !summary.hasData ? (
        <div className="empty-state" style={{ marginTop: 24 }}>
          <span className="empty-state-title">No location data for this person in the selected range</span>
        </div>
      ) : null}

      {summary?.hasData ? (
        <>
          <div className="stat-grid" style={{ marginTop: 16 }}>
            <StatCard label="Field Hours" value={summary.fieldHours} />
            <StatCard label="Shops Visited" value={summary.shopsVisited} />
            <StatCard label="Distance Covered" value={`${Number(summary.distanceKm).toFixed(2)} Km`} />
            <StatCard label="First In – Last Out" value={summary.firstLastTime} />
          </div>

          <div className="content-card" style={{ marginTop: 16 }}>
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 12, marginBottom: 10 }}>
              {LEGEND.map((item) => (
                <span key={item.label} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <span style={{ width: 12, height: 12, borderRadius: "50%", background: item.color, display: "inline-block" }} />
                  {item.label}
                </span>
              ))}
            </div>
            {summary.points.length ? (
              mapsKey ? <GoogleRouteMap points={summary.points} apiKey={mapsKey} /> : <SvgRouteMap points={summary.points} />
            ) : (
              <div className="page-subtitle">No GPS coordinates recorded.</div>
            )}

            <div style={{ marginTop: 18 }}>
              {/* one label per hour, placed where that hour falls on the strip */}
              <div style={{ position: "relative", height: 16, fontSize: 11, color: "var(--color-text-muted)" }}>
                {TIMELINE_HOURS.map((hour, index) => {
                  const last = index === TIMELINE_HOURS.length - 1;
                  return (
                    <span
                      key={hour}
                      style={{
                        position: "absolute",
                        left: `${(index / (TIMELINE_HOURS.length - 1)) * 100}%`,
                        transform: index === 0 ? "none" : last ? "translateX(-100%)" : "translateX(-50%)",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {hourLabel(hour)}
                    </span>
                  );
                })}
              </div>
              <div style={{ position: "relative", height: 18, background: "var(--color-neutral-soft)", borderRadius: 9 }}>
                {TIMELINE_HOURS.slice(1, -1).map((hour, index) => (
                  <span
                    key={hour}
                    style={{
                      position: "absolute",
                      left: `${((index + 1) / (TIMELINE_HOURS.length - 1)) * 100}%`,
                      top: 0,
                      width: 1,
                      height: "100%",
                      background: "var(--color-border, #d0d5dd)",
                    }}
                  />
                ))}
                {summary.timeline.map((point, index) => (
                  <span
                    key={index}
                    title={point.tooltip}
                    style={{
                      position: "absolute",
                      left: `calc(${point.leftPercent}% - 6px)`,
                      top: 3,
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      background: point.color,
                      border: "2px solid #fff",
                    }}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Shop</th>
                  <th>Location</th>
                </tr>
              </thead>
              <tbody>
                {summary.visits.length === 0 ? (
                  <tr>
                    <td colSpan={3}>No shops visited.</td>
                  </tr>
                ) : (
                  summary.visits.map((visit) => (
                    <tr key={visit.customerId}>
                      <td>
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 24,
                            height: 24,
                            borderRadius: "50%",
                            background: visit.color,
                            color: "#fff",
                            fontWeight: 700,
                            fontSize: 12,
                          }}
                        >
                          {visit.sequence}
                        </span>
                      </td>
                      <td>{visit.customerName}</td>
                      <td>
                        {visit.locationUrl ? (
                          <a href={visit.locationUrl} target="_blank" rel="noopener noreferrer">
                            Open in maps
                          </a>
                        ) : (
                          "-"
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default LocationSummary;
